import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { jsx as _jsx } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import chunk from "lodash/chunk";
import * as React from "react";
import styled from "styled-components";
import { BaseFiniteScrollCarousel } from "@faire/retailer/components/Carousel/FiniteScrollCarousel/BaseFiniteScrollCarousel";
import { Context } from "@faire/retailer/components/LayoutElementRenderer/__internal__/Context";
import { getTokensForViewEvent } from "../CarouselBase";
export var FiniteCarouselBase = function(props) {
    var _elementRef_current;
    var strictLocalize = useStrictLocalization().strictLocalize;
    var renderItem = props.renderItem, items = props.items, _props_rows = props.rows, rows = _props_rows === void 0 ? 1 : _props_rows, itemGap = props.itemGap, useIconArrows = props.useIconArrows, iconArrowsOffsetTop = props.iconArrowsOffsetTop;
    var _React_useContext = React.useContext(Context), layoutElement = _React_useContext.layoutElement, onVisible = _React_useContext.onVisible, requestId = _React_useContext.requestId;
    var _React_useState = _sliced_to_array(React.useState(false), 2), shouldHideRightButton = _React_useState[0], setShouldHideRightButton = _React_useState[1];
    var _React_useState1 = _sliced_to_array(React.useState(true), 2), shouldHideLeftButton = _React_useState1[0], setShouldHideLeftButton = _React_useState1[1];
    var _React_useState2 = _sliced_to_array(React.useState(false), 2), isOverflow = _React_useState2[0], setIsOverflow = _React_useState2[1];
    var elementRef = React.useRef(null);
    var handleComponentVisible = React.useCallback(function() {
        if (layoutElement.on_click_event_name) {
            var _getTokensForViewEvent_map, _getTokensForViewEvent;
            /**
             * The maximum length of a token in BE is 255 chars.
             * If you pass a token longer than that, then it will be removed from the event
             * and the event will be recorded without the long token.
             */ var tokensForViewEvent = (_getTokensForViewEvent = getTokensForViewEvent(layoutElement, strictLocalize)) === null || _getTokensForViewEvent === void 0 ? void 0 : (_getTokensForViewEvent_map = _getTokensForViewEvent.map(function(token) {
                return token === null || token === void 0 ? void 0 : token.replace(/\s/g, "_");
            })) === null || _getTokensForViewEvent_map === void 0 ? void 0 : _getTokensForViewEvent_map.join(" ").slice(0, 255);
            onVisible === null || onVisible === void 0 ? void 0 : onVisible(layoutElement.on_click_event_name, requestId, tokensForViewEvent !== null && tokensForViewEvent !== void 0 ? tokensForViewEvent : "");
        }
    }, [
        layoutElement,
        onVisible,
        requestId,
        strictLocalize
    ]);
    var viewRef = useDoActionWhenVisible(handleComponentVisible);
    React.useEffect(function() {
        if (elementRef.current) {
            setIsOverflow(elementRef.current.scrollWidth > Math.round(elementRef.current.getBoundingClientRect().width));
        } else {
            setIsOverflow(false);
        }
    }, [
        elementRef,
        (_elementRef_current = elementRef.current) === null || _elementRef_current === void 0 ? void 0 : _elementRef_current.scrollWidth
    ]);
    var itemsPerRow = Math.ceil(items.length / rows);
    if (items.length < 1) {
        return null;
    }
    var _layoutElement_on_click_event_name;
    return /*#__PURE__*/ _jsx("div", {
        ref: viewRef,
        children: /*#__PURE__*/ _jsx(StyledBaseFiniteScrollCarousel, {
            forwardRef: elementRef,
            name: (_layoutElement_on_click_event_name = layoutElement.on_click_event_name) !== null && _layoutElement_on_click_event_name !== void 0 ? _layoutElement_on_click_event_name : "",
            shouldHideRightButton: props.shouldHideRightButton || !isOverflow || shouldHideRightButton,
            shouldHideLeftButton: props.shouldHideLeftButton || !isOverflow || shouldHideLeftButton,
            onRightEndReached: function(isRightEndReached) {
                setShouldHideRightButton(isRightEndReached);
            },
            onLeftEndReached: function(isLeftEndReached) {
                setShouldHideLeftButton(isLeftEndReached);
            },
            chevronButtonDimension: {
                width: 15,
                height: 9
            },
            scrollConfiguration: props.scrollConfiguration,
            useIconArrows: useIconArrows,
            iconArrowsOffsetTop: iconArrowsOffsetTop,
            children: /*#__PURE__*/ _jsx(Column, {
                children: chunk(items, itemsPerRow).map(function(itemChunk, chunkIndex) {
                    return /*#__PURE__*/ _jsx(StyledRow, {
                        gap: itemGap,
                        children: itemChunk.map(renderItem)
                    }, "".concat(layoutElement.on_click_event_name, "-row-").concat(chunkIndex));
                })
            })
        })
    });
};
var StyledRow = styled(Row).attrs({
    align: "flex-start"
}).withConfig({
    componentId: "sc-793cf878-0"
})([
    "&:not(:first-child){margin-top:16px;}"
]);
var StyledBaseFiniteScrollCarousel = styled(BaseFiniteScrollCarousel.BaseFiniteScrollCarousel).withConfig({
    componentId: "sc-793cf878-1"
})([
    "display:flex;flex-direction:row;top:0;width:100%;padding:0;",
    ",",
    "{color:",
    ";width:30px;height:30px;",
    "{color:",
    ";&:hover{color:",
    ";}}",
    "{display:none;}}",
    "{left:-30px;}",
    "{right:-30px;}",
    "{align-items:flex-start;width:100%;}"
], BaseFiniteScrollCarousel.Previous, BaseFiniteScrollCarousel.Next, Core.text.enabled, responsiveMediaQueries.canHover, Core.text.subdued, Core.text.hover, responsiveMediaQueries.tabletAndBelow, BaseFiniteScrollCarousel.Previous, BaseFiniteScrollCarousel.Next, BaseFiniteScrollCarousel.Scrollable);
